import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

// --- Parse JSON to internal state ---
// `raw` (the parsed object, or {} on failure/empty) is carried alongside the
// edited fields so toJson can round-trip any top-level key this editor
// doesn't know about (e.g. "floor", seeded server-side and never exposed
// here). Without this, saving after touching ANY field silently drops those
// keys — see EntitlementRulesEditor round-trip fix.
function parseRules(raw) {
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== 'object') return { defaults: [], yearExtras: [], ageExtras: [], raw: {} };

    const defaults = Object.entries(obj.default || {})
      .map(([y, d]) => ({ years: String(y), days: String(d) }))
      .sort((a, b) => Number(a.years) - Number(b.years));

    const yearExtras = Object.entries(obj.extra?.forYears || {})
      .map(([y, d]) => ({ years: String(y), days: String(d) }));

    const ageExtras = [];
    if (obj.extra?.forAge) {
      const fa = obj.extra.forAge;
      ageExtras.push({ age: String(fa.age || 0), days: String(fa.entitlement || 0) });
    }
    // Support multiple age rules if stored as array
    if (Array.isArray(obj.extra?.forAges)) {
      obj.extra.forAges.forEach(fa => {
        ageExtras.push({ age: String(fa.age || 0), days: String(fa.entitlement || 0) });
      });
    }

    // floor: [{minAge?|maxAge?, value}] — "taban", kademeyi YÜKSELTİR.
    // Yön modeline çevrilebilenler düzenlenebilir satır olur; çevrilemeyenler
    // (iki sınırlı aralık kuralı, bozuk kayıt) floorOthers'ta AYNEN saklanır ve
    // salt-okunur gösterilir. Sessizce düşürmek veri kaybı olurdu.
    const floors = [];
    const floorOthers = [];
    if (Array.isArray(obj.floor)) {
      obj.floor.forEach(r => {
        const editable = editableFloor(r);
        if (editable) floors.push(editable);
        else floorOthers.push(r);
      });
    }

    return { defaults, yearExtras, ageExtras, floors, floorOthers, raw: obj };
  } catch {
    return { defaults: [], yearExtras: [], ageExtras: [], floors: [], floorOthers: [], raw: {} };
  }
}

// Bir floor kuralını yön modeline çevirir; çevrilemiyorsa null döner.
//
// `maxAge` ayrıştırıcıda HARİÇTİR (age < maxAge). Kullanıcıya ham sınırı
// göstermek doğrudan kanuni hataya yol açıyor: "onsekiz ve daha küçük" isteyen
// biri maxAge:18 yazar ve 18 yaşındakini kapsam dışında bırakır (tohumda
// gerçekten oldu). Bu yüzden gösterirken BİR EKSİLTİYOR, yazarken BİR
// EKLİYORUZ — kullanıcı kanunun lafzını görür, ayrıştırıcının değil.
function editableFloor(r) {
  if (!r || typeof r !== 'object') return null;
  if (typeof r.value !== 'number') return null;
  const hasMin = typeof r.minAge === 'number';
  const hasMax = typeof r.maxAge === 'number';
  if (hasMin === hasMax) return null; // ikisi birden ya da hiçbiri → temsil edilemez
  return hasMin
    ? { dir: 'min', age: String(r.minAge), days: String(r.value) }
    : { dir: 'max', age: String(r.maxAge - 1), days: String(r.value) };
}

function floorToRule(f) {
  const age = Number(f.age);
  const value = Number(f.days);
  return f.dir === 'max' ? { maxAge: age + 1, value } : { minAge: age, value };
}

// --- Build JSON from internal state ---
// Starts from the last-parsed raw object so unrecognized top-level keys
// (e.g. "floor") survive even though this editor has no UI for them —
// intentional scope decision: preservation is the fix, editing is a
// separate delivery. Only "default" and "extra", the keys this editor
// actually manages, are rebuilt from the current field state.
function toJson(defaults, yearExtras, ageExtras, floors, floorOthers, raw) {
  const obj = { ...(raw || {}) };
  delete obj.default;
  delete obj.extra;
  delete obj.floor;

  const validDefaults = defaults.filter(r => r.years !== '' && r.days !== '');
  if (validDefaults.length > 0) {
    obj.default = {};
    validDefaults.forEach(r => { obj.default[r.years] = Number(r.days); });
  }

  const validYears = yearExtras.filter(r => r.years !== '' && r.days !== '');
  const validAges = ageExtras.filter(r => r.age !== '' && r.days !== '');

  if (validYears.length > 0 || validAges.length > 0) {
    obj.extra = {};
    if (validYears.length > 0) {
      obj.extra.forYears = {};
      validYears.forEach(r => { obj.extra.forYears[r.years] = Number(r.days); });
    }
    if (validAges.length === 1) {
      obj.extra.forAge = { rule: 'greaterThan', age: Number(validAges[0].age), entitlement: Number(validAges[0].days) };
    } else if (validAges.length > 1) {
      obj.extra.forAges = validAges.map(r => ({ rule: 'greaterThan', age: Number(r.age), entitlement: Number(r.days) }));
    }
  }

  const validFloors = floors.filter(r => r.age !== '' && r.days !== '');
  const floorRules = [...validFloors.map(floorToRule), ...(floorOthers || [])];
  if (floorRules.length > 0) obj.floor = floorRules;

  return Object.keys(obj).length > 0 ? JSON.stringify(obj) : null;
}

export default function EntitlementRulesEditor({ value, onChange, label }) {
  const { t } = useTranslation();

  // Internal state — decoupled from JSON
  const [defaults, setDefaults] = useState([]);
  const [yearExtras, setYearExtras] = useState([]);
  const [ageExtras, setAgeExtras] = useState([]);
  // Yaş tabanı: yön modeline çevrilebilen kurallar düzenlenebilir satır olarak.
  const [floors, setFloors] = useState([]);
  // Tracks the most recent JSON that flowed through either direction, so
  // we can distinguish "parent pushed new value" from "our own emission
  // echoed back". Prevents infinite loop and also fixes the bug where a
  // one-shot `initialized` flag ignored the real value when the drawer
  // mounted with undefined first and then received the actual JSON.
  const syncedValueRef = useRef(null);
  // Top-level keys this editor doesn't understand (e.g. "floor"), carried
  // from the last parse so toJson can write them back untouched. See
  // parseRules/toJson comments.
  const rawRef = useRef({});
  // Yön modeliyle temsil edilemeyen floor kuralları (iki sınırlı aralık vb.).
  // Düzenlenmez ama ASLA düşürülmez — toJson bunları aynen geri yazar.
  const floorOthersRef = useRef([]);

  // Sync state from external value whenever the parent hands us something
  // new (e.g. drawer opened in edit mode and formData was populated a tick
  // after mount).
  useEffect(() => {
    if (value === syncedValueRef.current) return;
    const parsed = parseRules(value);
    setDefaults(parsed.defaults);
    setYearExtras(parsed.yearExtras);
    setAgeExtras(parsed.ageExtras);
    setFloors(parsed.floors);
    floorOthersRef.current = parsed.floorOthers;
    rawRef.current = parsed.raw;
    syncedValueRef.current = value;
  }, [value]);

  // Emit JSON whenever internal rule lists change. Skip if the produced
  // JSON is identical to what we last synced/emitted — avoids an infinite
  // loop with the sync effect above.
  useEffect(() => {
    const json = toJson(defaults, yearExtras, ageExtras, floors, floorOthersRef.current, rawRef.current);
    if (json === syncedValueRef.current) return;
    syncedValueRef.current = json;
    onChange(json);
  }, [defaults, yearExtras, ageExtras, floors]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      {label && <BoxLabel>{label}</BoxLabel>}

      {/* Section 1: Default entitlement by seniority year */}
      <Section>
        <SectionHead>
          <SectionTitle>{t('leaveVariation.defaultEntitlement')}</SectionTitle>
          <AddBtn type="button" onClick={() => setDefaults(prev => [...prev, { years: '', days: '' }])}>
            <Plus size={13} /> {t('leaveVariation.addRule')}
          </AddBtn>
        </SectionHead>
        {defaults.length === 0 && <Empty>{t('common.noRecords')}</Empty>}
        {defaults.map((r, i) => (
          <Row key={i}>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.seniorityYear')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                value={r.years}
                onChange={e => setDefaults(prev => prev.map((item, idx) => idx === i ? { ...item, years: e.target.value } : item))}
              />
            </FieldGroup>
            <Arrow>&rarr;</Arrow>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.entitlementDays')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                step="0.5"
                value={r.days}
                onChange={e => setDefaults(prev => prev.map((item, idx) => idx === i ? { ...item, days: e.target.value } : item))}
              />
            </FieldGroup>
            <DelBtn type="button" onClick={() => setDefaults(prev => prev.filter((_, idx) => idx !== i))}>
              <Trash2 size={14} />
            </DelBtn>
          </Row>
        ))}
      </Section>

      {/* Section 2: Extra days by seniority threshold */}
      <Section>
        <SectionHead>
          <SectionTitle>{t('leaveVariation.extraForYears')}</SectionTitle>
          <AddBtn type="button" onClick={() => setYearExtras(prev => [...prev, { years: '', days: '' }])}>
            <Plus size={13} /> {t('leaveVariation.addRule')}
          </AddBtn>
        </SectionHead>
        {yearExtras.length === 0 && <Empty>{t('common.noRecords')}</Empty>}
        {yearExtras.map((r, i) => (
          <Row key={i}>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.seniorityYear')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                value={r.years}
                onChange={e => setYearExtras(prev => prev.map((item, idx) => idx === i ? { ...item, years: e.target.value } : item))}
              />
            </FieldGroup>
            <Arrow>&rarr;</Arrow>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.extraDays')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                step="0.5"
                value={r.days}
                onChange={e => setYearExtras(prev => prev.map((item, idx) => idx === i ? { ...item, days: e.target.value } : item))}
              />
            </FieldGroup>
            <DelBtn type="button" onClick={() => setYearExtras(prev => prev.filter((_, idx) => idx !== i))}>
              <Trash2 size={14} />
            </DelBtn>
          </Row>
        ))}
      </Section>

      {/* Section 3: Extra days by age */}
      <Section>
        <SectionHead>
          <SectionTitle>{t('leaveVariation.extraForAge')}</SectionTitle>
          <AddBtn type="button" onClick={() => setAgeExtras(prev => [...prev, { age: '', days: '' }])}>
            <Plus size={13} /> {t('leaveVariation.addAgeRule')}
          </AddBtn>
        </SectionHead>
        {ageExtras.length === 0 && <Empty>{t('common.noRecords')}</Empty>}
        {ageExtras.map((r, i) => (
          <Row key={i}>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.ageThreshold')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                value={r.age}
                onChange={e => setAgeExtras(prev => prev.map((item, idx) => idx === i ? { ...item, age: e.target.value } : item))}
              />
            </FieldGroup>
            <Arrow>&rarr;</Arrow>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.extraDays')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                step="0.5"
                value={r.days}
                onChange={e => setAgeExtras(prev => prev.map((item, idx) => idx === i ? { ...item, days: e.target.value } : item))}
              />
            </FieldGroup>
            <DelBtn type="button" onClick={() => setAgeExtras(prev => prev.filter((_, idx) => idx !== i))}>
              <Trash2 size={14} />
            </DelBtn>
          </Row>
        ))}
      </Section>

      {/* Section 4: Age floor — RAISES the tier result, never lowers it */}
      <Section>
        <SectionHead>
          <SectionTitle>{t('leaveVariation.ageFloor')}</SectionTitle>
          <AddBtn
            type="button"
            data-testid="floor-add"
            onClick={() => setFloors(prev => [...prev, { dir: 'min', age: '', days: '' }])}
          >
            <Plus size={13} /> {t('leaveVariation.addFloorRule')}
          </AddBtn>
        </SectionHead>
        <Hint>{t('leaveVariation.ageFloorHint')}</Hint>
        {floors.length === 0 && floorOthersRef.current.length === 0 && <Empty>{t('common.noRecords')}</Empty>}
        {floors.map((r, i) => (
          <Row key={i}>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.floorAge')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                data-testid={`floor-age-${i}`}
                value={r.age}
                onChange={e => setFloors(prev => prev.map((item, idx) => idx === i ? { ...item, age: e.target.value } : item))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>&nbsp;</FieldLabel>
              <DirSelect
                data-testid={`floor-dir-${i}`}
                value={r.dir}
                onChange={e => setFloors(prev => prev.map((item, idx) => idx === i ? { ...item, dir: e.target.value } : item))}
              >
                <option value="min">{t('leaveVariation.floorAndAbove')}</option>
                <option value="max">{t('leaveVariation.floorAndBelow')}</option>
              </DirSelect>
            </FieldGroup>
            <Arrow>&rarr;</Arrow>
            <FieldGroup>
              <FieldLabel>{t('leaveVariation.minimumDays')}</FieldLabel>
              <NumberInput
                type="number"
                min="0"
                step="0.5"
                data-testid={`floor-days-${i}`}
                value={r.days}
                onChange={e => setFloors(prev => prev.map((item, idx) => idx === i ? { ...item, days: e.target.value } : item))}
              />
            </FieldGroup>
            <DelBtn
              type="button"
              data-testid={`floor-del-${i}`}
              onClick={() => setFloors(prev => prev.filter((_, idx) => idx !== i))}
            >
              <Trash2 size={14} />
            </DelBtn>
          </Row>
        ))}
        {/* Yön modeliyle gösterilemeyen kurallar: düzenlenmez ama görünür kalır ki
            kullanıcı politikasında böyle bir kural olduğunu bilsin. */}
        {floorOthersRef.current.map((r, i) => (
          <ReadOnlyRule key={`ro-${i}`} data-testid={`floor-readonly-${i}`}>
            {t('leaveVariation.floorCustomRule')}: <code>{JSON.stringify(r)}</code>
          </ReadOnlyRule>
        ))}
      </Section>
    </Box>
  );
}

const Box = styled.div`
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
  overflow: hidden;
`;

const BoxLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 12px 16px 0;
`;

const Section = styled.div`
  padding: 12px 16px;
  &:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SectionTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;
  &:hover { text-decoration: underline; }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 6px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FieldLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #94a3b8;
`;

const NumberInput = styled.input`
  width: 72px;
  height: 36px;
  padding: 0 10px;
  border: 1.5px solid #E0E4E8;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  background: #fff;
  &:focus { border-color: ${({ theme }) => theme.colors.primary.main}; }
  &::-webkit-inner-spin-button { opacity: 1; }
`;

const Arrow = styled.span`
  font-size: 16px;
  color: #cbd5e1;
  padding-bottom: 6px;
`;

const DelBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(229, 57, 53, 0.08); color: #E53935; }
`;

const Hint = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
  line-height: 1.45;
`;

const DirSelect = styled.select`
  height: 36px;
  padding: 0 8px;
  border: 1.5px solid #E0E4E8;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  background: #fff;
  cursor: pointer;
  &:focus { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const ReadOnlyRule = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #64748b;
  background: #F5F7FA;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
  code { font-size: 11px; }
`;

const Empty = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  padding: 4px 0;
`;
